import React from 'react';
import { useSelector } from 'react-redux';
import { Divider } from 'housecall-ui';
import Typography from 'housecall-ui/dist/DesignSystem/DataDisplay/Typography/Typography';
import Box from 'housecall-ui/dist/DesignSystem/Layout/Box/Box';
import makeStyles from 'housecall-ui/dist/DesignSystem/Styles/makeStyles';
import { formatCurrency } from 'utils/currency';

import { UNSUPPORTED } from 'components/PayWall/Redux/Data';
import {
  PayWallAdditionalUsers,
  PayWallDuration,
  PayWallFullState,
  PayWallPlan,
  PayWallTiers,
} from 'components/PayWall/Redux/Reducer.type';
import {
  getPayWallAddonsFeaturesV3 as getPayWallAddonsFeatures,
  getPlanSummaryPreview,
  getSelectedPlanV3,
} from 'components/PayWall/Redux/Selectors';

import usePriceDisplayCadence from './hooks/usePriceDisplayCadence';
import { PER_YEAR } from './hooks/variants';
import UnsupportedTotal from './PlanSummaryTotal/UnsupportedTotal';

const useTypographyStyles = makeStyles((theme) => ({
  subtitle: {
    fontWeight: 400,
    fontSize: 24,
  },
  emphasized: {
    fontWeight: 600,
    fontSize: 24,
  },
  caption: {
    marginLeft: theme.spacing(0.5),
  },
}));

type PriceDisplayProps = {
  price: string | React.ReactNode;
  duration: string;
  strikeThrough?: boolean;
  emphasize?: boolean;
  enlarge?: boolean;
};

type PriceDisplayStyleProps = {
  strikeThrough: boolean;
  emphasize: boolean;
  enlarge: boolean;
};

const usePriceDisplayStyles = makeStyles(() => ({
  priceDisplay: {
    textDecoration: ({ strikeThrough }: PriceDisplayStyleProps) => (strikeThrough ? 'line-through' : 'none'),
  },
  priceValue: {
    fontWeight: ({ emphasize }: PriceDisplayStyleProps) => (emphasize ? 'bold' : 'normal'),
    fontSize: ({ enlarge }: PriceDisplayStyleProps) => (enlarge ? 34 : 'inherit'),
  },
}));

const getDurationText = (duration: string) => {
  const dict = {
    [PayWallDuration.SixMonths]: 'per six months',
    [PayWallDuration.Monthly]: 'per month',
    [PayWallDuration.Annual]: 'per year',
  };

  return dict[duration] || duration;
};

export function PriceDisplay({
  price,
  duration,
  strikeThrough = false,
  emphasize = false,
  enlarge = false,
}: PriceDisplayProps) {
  const classes = usePriceDisplayStyles({ strikeThrough, emphasize, enlarge });

  return (
    <Box display="flex" justifyContent="flex-end" alignItems="baseline" className={classes.priceDisplay}>
      <Typography
        variant="body1"
        aria-label="upsell feature price"
        align="right"
        data-testid="price-amount"
        className={classes.priceValue}
      >
        {price}
      </Typography>
      <Box ml={0.5}>
        <Typography variant="caption" data-testid="price-cadence">
          {getDurationText(duration)}
        </Typography>
      </Box>
    </Box>
  );
}

function AdditionalUsers(props: { additionalUsers: PayWallAdditionalUsers }) {
  const priceDisplayVariant = usePriceDisplayCadence();

  const { additionalUsers } = props;
  const summaryPreview = useSelector(getPlanSummaryPreview);

  const { plan_type: planType } = summaryPreview;
  const useAnnualPricing = planType === 'annual' && priceDisplayVariant === PER_YEAR;

  const duration = useAnnualPricing ? PayWallDuration.Annual : PayWallDuration.Monthly;
  const priceMultiplier = useAnnualPricing ? 12 : 1;

  if (!additionalUsers || additionalUsers.count < 1) {
    return null;
  }

  return (
    <Box
      display="flex"
      justifyContent="space-between"
      aria-label="additional logins addon"
      flexWrap="nowrap"
      data-testid="additional-logins"
    >
      <Typography variant="body1">
        Additional logins (x<span data-testid="count">{additionalUsers.count}</span>)
      </Typography>
      <PriceDisplay
        price={formatCurrency(additionalUsers.amount * priceMultiplier)}
        duration={duration}
        data-testid="additional-logins-amount"
      />
    </Box>
  );
}

function UpsellFeatures() {
  const priceDisplayVariant = usePriceDisplayCadence();

  const payWallAddonsFeatures = useSelector(getPayWallAddonsFeatures);
  const summaryPreview = useSelector(getPlanSummaryPreview);

  const { plan_type: planType } = summaryPreview;

  const useAnnualPricing = planType === 'annual' && priceDisplayVariant === PER_YEAR;

  const duration = useAnnualPricing ? PayWallDuration.Annual : PayWallDuration.Monthly;
  const priceMultiplier = useAnnualPricing ? 12 : 1;

  const features = payWallAddonsFeatures
    .filter((feature) => feature.enabled || feature.included)
    .map(({ price, displayName, quantifiable = false, included, quantity = 1 }) => (
      <Box
        display="flex"
        justifyContent="space-between"
        data-testid="upsell-features"
        aria-label="upsell feature addon"
        key={`upsell_feature_${displayName}`}
      >
        <Typography variant="body1">
          {displayName}{' '}
          {quantifiable && (
            <>
              (x<span data-testid="count">{quantity}</span>)
            </>
          )}
          {included && <>(included)</>}
        </Typography>
        <PriceDisplay price={formatCurrency((price * quantity * priceMultiplier) / 100.0)} duration={duration} />
      </Box>
    ));

  return <>{features}</>;
}

function Addons() {
  const orgFeatures = useSelector((state: PayWallFullState) => state.payWall.planSummaryPreview.additional_features);
  const additionalUsers = useSelector((state: PayWallFullState) => state.payWall.planSummaryPreview.additional_users);

  if (!orgFeatures?.length && !additionalUsers?.count) {
    return null;
  }

  return (
    <Box data-testid="addons-container">
      <AdditionalUsers additionalUsers={additionalUsers} />
      <UpsellFeatures />
    </Box>
  );
}

function Totals() {
  const { subtitle, emphasized, caption } = useTypographyStyles();
  const summaryPreview = useSelector(getPlanSummaryPreview);

  const {
    when_to_apply: whenToApplyChange,
    recurring_sales_tax: reccuringTax,
    recurring_amount_gross: reccuringAmount,
    plan_type: planType,
    renewal_date: renewalDate,
  } = summaryPreview;

  const duration = planType === 'annual' ? PayWallDuration.Annual : PayWallDuration.Monthly;
  const dueType = planType === 'annual' ? 'annually' : 'monthly';

  if (whenToApplyChange === UNSUPPORTED) {
    return <UnsupportedTotal />;
  }

  return (
    <>
      <Typography variant="h3" gutterBottom className={subtitle}>
        Totals
      </Typography>
      <Box display="flex" justifyContent="space-between">
        <Typography variant="body1">Taxes</Typography>
        <PriceDisplay price={formatCurrency(reccuringTax)} duration={duration} />
      </Box>
      <Box display="flex" justifyContent="space-between" alignItems="baseline" mt={2}>
        <Box display="flex" alignItems="baseline">
          <Typography variant="subtitle1" className={emphasized}>
            Plan total
          </Typography>
          <Typography variant="body1" className={caption} data-testid="renewal-date">
            (due {dueType}
            <>{renewalDate && <> starting {renewalDate}</>}</>)
          </Typography>
        </Box>
        <PriceDisplay price={formatCurrency(reccuringAmount)} duration={duration} emphasize />
      </Box>
    </>
  );
}

function DueToday() {
  const { emphasized, caption } = useTypographyStyles();
  const summaryPreview = useSelector(getPlanSummaryPreview);

  const {
    total_due_today_gross: totalDueToday,
    renewal_date: renewalDate,
    activation_fee_to_display: activationFee,
    skip_setup_fee: skipSetupFee,
    prorated_balance: proratedBalance,
  } = summaryPreview;

  return (
    <>
      {!skipSetupFee && activationFee && Number(activationFee) > 0 && (
        <Box display="flex" justifyContent="space-between">
          <Typography variant="body1">Activation fee</Typography>
          <PriceDisplay price={formatCurrency(activationFee)} duration="" />
        </Box>
      )}
      <Box display="flex" justifyContent="space-between" alignItems="baseline">
        <Box>
          <Box display="flex" alignItems="baseline">
            <Typography variant="subtitle1" className={emphasized}>
              Due today
            </Typography>
            {Number(proratedBalance) > 0 && (
              <Typography variant="body1" className={caption}>
                (prorated charge)
              </Typography>
            )}
          </Box>
          {renewalDate && (
            <Typography variant="caption" data-testid="renewal-date">
              This amount covers today through {renewalDate}
            </Typography>
          )}
        </Box>
        <PriceDisplay
          price={formatCurrency(Number(totalDueToday) <= 0 ? 0 : totalDueToday)}
          duration=""
          emphasize
          enlarge
        />
      </Box>
    </>
  );
}

function getUsersInPlan(selectedPlan: PayWallPlan) {
  let userCount = 'Unlimited logins';

  if (selectedPlan.tier !== PayWallTiers.EXTRA_LARGE_UNLIMITED) {
    if (selectedPlan.number_of_free_seats === 1) {
      userCount = '1 login';
    } else if (selectedPlan.number_of_free_seats > 1) {
      userCount = `up to ${selectedPlan.number_of_free_seats} logins`;
    }
  }

  return userCount;
}

export default function PayWallPlanSummary(): JSX.Element {
  const planAmount = useSelector((state: PayWallFullState) => state.payWall.planSummaryPreview.plan_amount);
  const selectedPlan = useSelector(getSelectedPlanV3);
  const summaryPreview = useSelector(getPlanSummaryPreview);

  const { plan_type: planType } = summaryPreview;

  const duration = planType === 'annual' ? PayWallDuration.Annual : PayWallDuration.Monthly;
  const priceMultiplier = planType === 'annual' ? 12 : 1;

  return (
    <Box>
      <Typography variant="h2" gutterBottom>
        Plan details
      </Typography>

      <Box display="flex" justifyContent="space-between">
        <Typography variant="body1">
          {selectedPlan.display_name} plan ({getUsersInPlan(selectedPlan)})
          {selectedPlan.discount_label && (
            <Typography variant="caption" data-testid="partner-discount" gutterBottom>
              Includes a ${selectedPlan.discount_label} preferred partner discount.
            </Typography>
          )}
        </Typography>
        <PriceDisplay price={formatCurrency(Number(planAmount) * priceMultiplier)} duration={duration} />
      </Box>

      <Addons />

      <Box my={2}>
        <Divider />
      </Box>

      <Totals />

      <Box my={2}>
        <Divider />
      </Box>

      <DueToday />
    </Box>
  );
}
